import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBolt,
  FaEye,
  FaShieldAlt,
  FaClock,
  FaArrowRight,
  FaStar,
  FaCheckCircle,
  FaTachometerAlt,
  FaHeart,
  FaRegHeart
} from 'react-icons/fa';

// Helper to create URL‑friendly slug (must match the one in SpecialPowerDetail)
const createSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const SpecialPowerPage = () => {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);

  const products = [
    {
      id: 1,
      name: 'Blue Cut Glasses',
      description: 'Protect eyes from harmful blue light',
      image: 'https://img.freepik.com/premium-photo/black-frame-eyeglasses-unisex-isolated-white-background_727279-100.jpg?w=200',
      price: '₹1,999',
      originalPrice: '₹3,999',
      discount: '50%',
      rating: 4.7,
      reviews: 1250,
      features: ['UV Protection', 'Anti-glare', 'Digital Screen Protection']
    },
    {
      id: 2,
      name: 'Photochromic Lenses',
      description: 'Auto-adjust to light conditions',
      image: 'https://www.sojosvision.com/cdn/shop/products/C3A_ad8a94ef-0f1f-4498-aecc-4457d8909af2_600x.jpg?v=1638430752',
      price: '₹3,499',
      originalPrice: '₹5,999',
      discount: '42%',
      rating: 4.8,
      reviews: 850,
      features: ['Indoor/Outdoor', 'UV400', 'Auto-darkening']
    },
    {
      id: 3,
      name: 'Polarized Sunglasses',
      description: 'Reduce glare while driving',
      image: 'https://improb.com/wp-content/uploads/2019/04/Sunski-Makani-Tortoise-Frame-Polarized-Sunglasses.jpg',
      price: '₹2,499',
      originalPrice: '₹4,499',
      discount: '44%',
      rating: 4.6,
      reviews: 620,
      features: ['100% UV Protection', 'Anti-glare', 'Driving Essential']
    },
    {
      id: 4,
      name: 'High Index Lenses',
      description: 'Thinner & lighter for high power',
      image: 'https://media.istockphoto.com/id/1410211638/photo/two-green-gradient-sun-protection-plastic-eyeglass-lens-on-black-background.jpg?s=170667a&w=0&k=20&c=0vTaGQ61IWo07_9HGXhwFj5lRjm2O6BHSp2DpoB3k6A=',
      price: '₹4,999',
      originalPrice: '₹7,999',
      discount: '38%',
      rating: 4.9,
      reviews: 430,
      features: ['Ultra-thin', 'Lightweight', 'Crystal Clear']
    },
    {
      id: 5,
      name: 'Progressive Lenses',
      description: 'Seamless vision for all distances',
      image: 'https://th.bing.com/th/id/R.a1d7bcb15be5ecb62cc46b201f8dfb09?rik=WU6CeDZud6x64g&riu=http%3a%2f%2fpurepng.com%2fpublic%2fuploads%2flarge%2fpurepng.com-glassesglasseseyeglassesspectaclesplastic-lensesmounted-14215264993951jk1l.png&ehk=g5RptwatIBbZIyZt5zZ1ebSVDPHnhPbr2j1jR14b4r8%3d&risl=&pid=ImgRaw&r=0',
      price: '₹5,499',
      originalPrice: '₹8,999',
      discount: '39%',
      rating: 4.8,
      reviews: 310,
      features: ['Distance/Near', 'No Lines', 'Smooth Transition']
    },
    {
      id: 6,
      name: 'Anti-Fatigue Lenses',
      description: 'Reduce eye strain while reading',
      image: 'https://www.zennioptical.com/blog/wp-content/uploads/2024/10/eye-test_Featured-Image.jpg',
      price: '₹2,999',
      originalPrice: '₹4,999',
      discount: '40%',
      rating: 4.7,
      reviews: 520,
      features: ['Eye Comfort', 'Reading Support', 'Digital Protection']
    }
  ];

  const features = [
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: 'UV Protection',
      description: '100% protection from harmful UV rays'
    },
    {
      icon: <FaEye className="text-3xl" />,
      title: 'Blue Cut Technology',
      description: 'Protection from digital screen blue light'
    },
    {
      icon: <FaTachometerAlt className="text-3xl" />,
      title: 'Anti-Glare',
      description: 'Reduce reflection and eye strain'
    },
    {
      icon: <FaClock className="text-3xl" />,
      title: 'Long Lasting',
      description: 'Durable coating and scratch resistant'
    }
  ];

  const toggleWishlist = (productId, e) => {
    e.stopPropagation();
    setWishlist(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const openProductDetail = (product) => {
    const slug = createSlug(product.name);
    navigate(`/special-power/${slug}`, { state: { product } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FaBolt className="text-5xl mx-auto mb-4 animate-bounce" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Special Power Lenses</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            Advanced technology lenses for every need
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="text-purple-600 mb-4">{feature.icon}</div>
              <h3 className="font-semibold text-lg text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-500">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Special Power Collection</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Choose the perfect lenses for your vision needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              onClick={() => openProductDetail(product)}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
            >
              <div className="relative h-48 bg-gradient-to-br from-purple-50 to-blue-50 p-6">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                />
                <button
                  onClick={(e) => toggleWishlist(product.id, e)}
                  className="absolute top-2 right-2 z-10 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:scale-110 transition-transform"
                >
                  {wishlist.includes(product.id) ? (
                    <FaHeart className="text-red-500 text-sm" />
                  ) : (
                    <FaRegHeart className="text-gray-400 text-sm" />
                  )}
                </button>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{product.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{product.description}</p>
                <div className="space-y-1 mb-4">
                  {product.features.map((feature, i) => (
                    <p key={i} className="text-xs text-gray-600 flex items-center gap-1">
                      <FaCheckCircle className="text-green-500 text-xs" />
                      {feature}
                    </p>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <span className="text-2xl font-bold text-gray-900">{product.price}</span>
                    <span className="ml-2 text-sm text-gray-400 line-through">
                      {product.originalPrice}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openProductDetail(product);
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    Shop Now
                    <FaArrowRight className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpecialPowerPage;